import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import React from 'react';
import { motion } from 'framer-motion';

interface IntroCardProps {
    title: string;
    titleLine2?: string;
    description: React.ReactNode;
    onClose: () => void;
    className?: string;
    actions?: React.ReactNode;
}

export const IntroCard: React.FC<IntroCardProps> = ({
    title,
    titleLine2,
    description,
    onClose,
    className,
    actions
}) => {
    return (
        <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn("w-full mb-8", className)}
        >
            <Card className="relative overflow-hidden border-border bg-card shadow-sm group/container">
                {/* Unified Glass Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background/50 pointer-events-none" />
                
                {/* Decorative Elements */}
                <div className="absolute right-0 top-0 p-32 bg-gradient-to-b from-primary/5 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-50" />

                {/* Dismiss Button */}
                <div className="absolute top-3 right-3 z-20">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        className="h-8 w-8 text-muted-foreground/30 hover:text-foreground hover:bg-background/80 rounded-full transition-colors"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                <CardContent className="relative p-0">
                    <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[160px]">
                        {/* Content Section */}
                        <div className="lg:col-span-8 p-8 flex flex-col justify-center">
                            <div className="space-y-3">
                                <div className="space-y-0.5">
                                    <h2 className="text-2xl font-bold tracking-tight text-foreground leading-[1.1]">
                                        {title}
                                        {titleLine2 && (
                                            <span className="block italic font-lora font-normal text-muted-foreground/90 py-1">
                                                {titleLine2}
                                            </span>
                                        )}
                                    </h2>
                                </div>
                                <div className="text-muted-foreground/80 text-[15px] leading-relaxed max-w-[560px] font-normal">
                                    {description}
                                </div>
                            </div>
                        </div>

                        {/* Actions Section */}
                        {actions && (
                            <div className="lg:col-span-4 p-8 bg-muted/5 border-t lg:border-t-0 lg:border-l border-border/40 flex flex-col justify-center">
                                <div className="w-full">
                                    {actions}
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};
